import React, {
  useState,
  useCallback,
  useEffect,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  UserRound,
  AlertTriangle,
  Apple,
  ArrowRight,
  Crown,
  Check,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import API from "../../services/api";
import { useAuth } from "../../context/AuthContext";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import logo from "../../assets/logo.png";
import bg from "../../assets/bg.png";

import "../../styles/login.css";


export default function Login() {
  const navigate = useNavigate();

  const {
    login: setAuth,
    isAuthenticated,
    user,
  } = useAuth();

  const role = user?.role?.toLowerCase();


  /* =========================================================
     FORMULARIO
  ========================================================= */

  const [form, setForm] = useState({
    identifier: "",
    password: "",
  });


  /* =========================================================
     ESTADOS
  ========================================================= */

  const [rememberMe, setRememberMe] = useState(false);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [mounted, setMounted] = useState(false);


  /* =========================================================
     ANIMACIÓN INICIAL
  ========================================================= */

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, []);


  /* =========================================================
     AUTO REDIRECT
  ========================================================= */

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const redirectPath =
      localStorage.getItem(
        "redirectAfterLogin"
      );

    if (redirectPath) {
      localStorage.removeItem(
        "redirectAfterLogin"
      );

      navigate(redirectPath, {
        replace: true,
      });

      return;
    }


    /* =======================================================
       REDIRECCIÓN SEGÚN ROL
    ======================================================= */

    switch (role) {
      case "admin":
        navigate("/admin", {
          replace: true,
        });
        break;

      case "trabajador":
        navigate("/worker", {
          replace: true,
        });
        break;

      case "cliente":
        navigate("/catalog", {
          replace: true,
        });
        break;

      default:
        navigate("/", {
          replace: true,
        });
        break;
    }

  }, [
    isAuthenticated,
    role,
    navigate,
  ]);


  /* =========================================================
     CAMBIO DE INPUT
  ========================================================= */

  const handleChange = useCallback(
    (event) => {
      const {
        name,
        value,
      } = event.target;

      setForm((previous) => ({
        ...previous,
        [name]: value,
      }));

      setError("");
    },
    []
  );


  /* =========================================================
     VALIDACIÓN
  ========================================================= */

  const validateForm = useCallback(() => {

    if (!form.identifier.trim()) {
      setError(
        "Ingresa tu correo electrónico o usuario."
      );

      return false;
    }

    if (!form.password.trim()) {
      setError(
        "Ingresa tu contraseña."
      );

      return false;
    }

    return true;

  }, [form]);


  /* =========================================================
     LOGIN
  ========================================================= */

  const loginRequest = useCallback(
    async () => {

      if (loading) {
        return;
      }

      if (!validateForm()) {
        return;
      }

      setLoading(true);
      setError("");

      try {

        /* ===================================================
           PETICIÓN
        =================================================== */

        const response = await API.post(
          "auth/login/",
          {
            identifier:
              form.identifier.trim(),

            password:
              form.password,
          }
        );


        /* ===================================================
           RESPUESTA
        =================================================== */

        const payload =
          response.data?.data;


        if (!payload?.access) {
          throw new Error(
            "Respuesta de autenticación inválida."
          );
        }


        /* ===================================================
           DATOS DE AUTENTICACIÓN
        =================================================== */

        const authData = {
          access:
            payload.access,

          refresh:
            payload.refresh,

          user:
            payload.user,
        };


        /* ===================================================
           RECORDAR SESIÓN
        =================================================== */

        localStorage.setItem(
          "rememberMe",
          rememberMe
            ? "true"
            : "false"
        );


        /* ===================================================
           GUARDAR AUTH
        =================================================== */

        setAuth(authData);


        /* ===================================================
           ROL
        =================================================== */

        const authenticatedRole =
          payload.user?.role?.toLowerCase();


        /* ===================================================
           REDIRECT AFTER LOGIN
        =================================================== */

        const redirectPath =
          localStorage.getItem(
            "redirectAfterLogin"
          );


        if (redirectPath) {

          localStorage.removeItem(
            "redirectAfterLogin"
          );

          navigate(
            redirectPath,
            {
              replace: true,
            }
          );

          return;
        }


        /* ===================================================
           REDIRECT SEGÚN ROL
        =================================================== */

        switch (authenticatedRole) {

          case "admin":

            navigate(
              "/admin",
              {
                replace: true,
              }
            );

            break;


          case "trabajador":

            navigate(
              "/worker",
              {
                replace: true,
              }
            );

            break;


          case "cliente":

            navigate(
              "/catalog",
              {
                replace: true,
              }
            );

            break;


          default:

            navigate(
              "/",
              {
                replace: true,
              }
            );

            break;
        }

      } catch (err) {

        console.error(
          "LOGIN ERROR:",
          err.response?.data ||
          err
        );

        setError(
          err.response?.data
            ?.errors
            ?.non_field_errors
            ?.[0] ||

          err.response?.data
            ?.errors
            ?.identifier
            ?.[0] ||

          err.response?.data
            ?.message ||

          err.response?.data
            ?.detail ||

          "Las credenciales ingresadas no son válidas."
        );

      } finally {

        setLoading(false);

      }

    },
    [
      loading,
      validateForm,
      form,
      rememberMe,
      setAuth,
      navigate,
    ]
  );


  /* =========================================================
     ENTER PARA LOGIN
  ========================================================= */

  const handleKeyDown = useCallback(
    (event) => {

      if (
        event.key === "Enter"
      ) {
        event.preventDefault();
        loginRequest();
      }

    },
    [loginRequest]
  );


  /* =========================================================
     REGISTRO
  ========================================================= */

  const goToRegister =
    useCallback(() => {

      navigate("/register");

    }, [navigate]);


  /* =========================================================
     RECUPERAR CONTRASEÑA
  ========================================================= */

  const handleForgotPassword =
    useCallback(() => {

      navigate(
        "/forgot-password"
      );

    }, [navigate]);


  /* =========================================================
     GOOGLE
  ========================================================= */

  const handleGoogleLogin =
    useCallback(() => {

      setError(
        "El inicio de sesión con Google todavía no está configurado."
      );

    }, []);


  /* =========================================================
     APPLE
  ========================================================= */

  const handleAppleLogin =
    useCallback(() => {

      setError(
        "El inicio de sesión con Apple todavía no está configurado."
      );

    }, []);


  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className={`login-page ${
        mounted
          ? "login-page--visible"
          : ""
      }`}
      style={{
        backgroundImage:
          `url(${bg})`,
      }}
    >

      {/* =====================================================
          CAPAS DEL FONDO
      ===================================================== */}

      <div className="login-page__overlay" />

      <div className="login-page__noise" />

      <div className="login-page__scanlines" />


      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <Navbar />


      {/* =====================================================
          CONTENIDO PRINCIPAL
      ===================================================== */}

      <main className="login-main">

        <section className="login-card">

          {/* =================================================
              DECORACIÓN SUPERIOR
          ================================================= */}

          <div className="login-card__top-line" />

          <div className="
            login-card__corner
            login-card__corner--tl
          " />

          <div className="
            login-card__corner
            login-card__corner--tr
          " />

          <div className="
            login-card__corner
            login-card__corner--bl
          " />

          <div className="
            login-card__corner
            login-card__corner--br
          " />


          {/* =================================================
              CABECERA
          ================================================= */}

          <div className="login-card__header">

            <div className="login-logo-wrap">

              <div className="login-logo-crown">
                <Crown size={28} />
              </div>

              <img
                src={logo}
                alt="Debuts Crop"
                className="login-logo"
              />

            </div>


            <div className="login-heading">

              <div className="login-heading__eyebrow">
                <Sparkles size={13} />

                <span>
                  DEBUTS CROP · ACCESS
                </span>
              </div>

              <h1>
                INICIAR SESIÓN
              </h1>

              <p>
                Accede a tu cuenta para continuar
              </p>

            </div>

          </div>


          {/* =================================================
              FORMULARIO
          ================================================= */}

          <div className="login-form">


            {/* ===============================================
                USUARIO / CORREO
            =============================================== */}

            <div
              className={`login-input ${
                form.identifier
                  ? "login-input--filled"
                  : ""
              }`}
            >

              <UserRound
                size={20}
                className="login-input__icon"
              />

              <input
                type="text"
                name="identifier"
                value={
                  form.identifier
                }
                placeholder="Correo electrónico o usuario"
                onChange={
                  handleChange
                }
                onKeyDown={
                  handleKeyDown
                }
                disabled={loading}
                autoComplete="username"
                aria-label="Correo electrónico o usuario"
              />

            </div>


            {/* ===============================================
                CONTRASEÑA
            =============================================== */}

            <div
              className={`login-input ${
                form.password
                  ? "login-input--filled"
                  : ""
              }`}
            >

              <LockKeyhole
                size={20}
                className="login-input__icon"
              />

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                name="password"
                value={
                  form.password
                }
                placeholder="Contraseña"
                onChange={
                  handleChange
                }
                onKeyDown={
                  handleKeyDown
                }
                disabled={loading}
                autoComplete="current-password"
                aria-label="Contraseña"
              />


              <button
                type="button"
                className="login-input__toggle"
                onClick={() =>
                  setShowPassword(
                    (previous) =>
                      !previous
                  )
                }
                aria-label={
                  showPassword
                    ? "Ocultar contraseña"
                    : "Mostrar contraseña"
                }
              >

                {showPassword ? (
                  <EyeOff size={19} />
                ) : (
                  <Eye size={19} />
                )}

              </button>

            </div>


            {/* ===============================================
                OPCIONES
            =============================================== */}

            <div className="login-options">

              <label className="login-remember">

                <input
                  type="checkbox"
                  checked={
                    rememberMe
                  }
                  onChange={(event) =>
                    setRememberMe(
                      event.target.checked
                    )
                  }
                  disabled={loading}
                />

                <span className="login-checkbox">

                  {rememberMe && (
                    <Check size={12} />
                  )}

                </span>

                <span>
                  Recordarme
                </span>

              </label>


              <button
                type="button"
                className="login-forgot"
                onClick={
                  handleForgotPassword
                }
              >
                ¿Olvidaste tu contraseña?
              </button>

            </div>


            {/* ===============================================
                ERROR
            =============================================== */}

            {error && (

              <div className="login-error">

                <AlertTriangle
                  size={17}
                />

                <span>
                  {error}
                </span>

              </div>

            )}


            {/* ===============================================
                BOTÓN PRINCIPAL
            =============================================== */}

            <button
              type="button"
              className="login-button"
              onClick={
                loginRequest
              }
              disabled={loading}
            >

              {loading ? (

                <>
                  <Loader2
                    size={20}
                    className="login-spinner"
                  />

                  <span>
                    VALIDANDO...
                  </span>
                </>

              ) : (

                <>
                  <span>
                    INICIAR SESIÓN
                  </span>

                  <ArrowRight
                    size={21}
                  />
                </>

              )}

            </button>


            {/* ===============================================
                SEGURIDAD
            =============================================== */}

            <div className="login-security">

              <ShieldCheck size={16} />

              <span>
                Acceso protegido · Debuts Crop
              </span>

            </div>


            {/* ===============================================
                DIVISOR
            =============================================== */}

            <div className="login-divider">

              <span />

              <b>
                O
              </b>

              <span />

            </div>


            {/* ===============================================
                GOOGLE
            =============================================== */}

            <button
              type="button"
              className="login-social"
              onClick={
                handleGoogleLogin
              }
              disabled={loading}
            >

              <span
                className="
                  login-social__icon
                  login-social__icon--google
                "
              >
                <span className="google-letter">
                  G
                </span>
              </span>

              <span>
                Continuar con Google
              </span>

            </button>


            {/* ===============================================
                APPLE
            =============================================== */}

            <button
              type="button"
              className="login-social"
              onClick={
                handleAppleLogin
              }
              disabled={loading}
            >

              <span
                className="
                  login-social__icon
                  login-social__icon--apple
                "
              >
                <Apple size={20} />
              </span>

              <span>
                Continuar con Apple
              </span>

            </button>


            {/* ===============================================
                REGISTRO
            =============================================== */}

            <div className="login-register">

              <span>
                ¿No tienes una cuenta?
              </span>

              <button
                type="button"
                onClick={
                  goToRegister
                }
              >
                Crear cuenta
              </button>

            </div>

          </div>


          {/* =================================================
              PIE DE TARJETA
          ================================================= */}

          <div className="login-card__bottom">

            <span>
              TU CULTIVO
            </span>

            <i>
              ·
            </i>

            <span>
              TU ESTILO
            </span>

          </div>

        </section>

      </main>


      {/* =====================================================
          FOOTER GLOBAL
      ===================================================== */}

      <Footer />

    </div>
  );
}